'use client';

import {
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useBannerSort } from '~/entities/Banners/hooks';
import {
  StyledHeaderBoxTableCell,
  StyledHeaderTableCell,
  StyledTable,
} from '~/shared';
import SortIcon from '~/shared/icons/SortIcon';

import { useGetDashboard } from '../hooks';

import { DashListRow } from './DashListRow';

export type BetterCampaignListProps = {
  getDashboardQuery: ReturnType<typeof useGetDashboard>;
};

export function DashList({ getDashboardQuery }: BetterCampaignListProps) {
  const { data, isLoading, isError, error } = getDashboardQuery;

  const {
    sortedBanners: sortedCampaigns,
    sortByOrderName,
    sortByOrderImpressions,
    sortByOrderClicks,
    sortByOrderCTR,
    setSortByOrderName,
    setSortByOrderImpressions,
    setSortByOrderClicks,
    setSortByOrderCTR,
    toggleSort,
  } = useBannerSort(data?.bestCampaigns);

  return (
    <StyledTable aria-label="Tabela de Banners">
      <TableHead>
        <TableRow>
          <StyledHeaderTableCell sx={{ width: '60px' }} align="center">
            ID
          </StyledHeaderTableCell>
          <StyledHeaderTableCell
            sx={{
              minWidth: '200px',
              position: 'sticky',
              left: 0,
              backgroundColor: 'background.paper',
            }}
          >
            <StyledHeaderBoxTableCell sx={{ justifyContent: 'flex-start' }}>
              <Typography variant="body2" component="p" fontWeight={500}>
                Anunciante / Campanha
              </Typography>
              <SortIcon
                sortOrder={sortByOrderName}
                onClick={() => toggleSort(sortByOrderName, setSortByOrderName)}
              />
            </StyledHeaderBoxTableCell>
          </StyledHeaderTableCell>
          <StyledHeaderTableCell align="center">
            <StyledHeaderBoxTableCell>
              <Typography variant="body2" component="p" fontWeight={500}>
                Impressões
              </Typography>
              <SortIcon
                sortOrder={sortByOrderImpressions}
                onClick={() =>
                  toggleSort(sortByOrderImpressions, setSortByOrderImpressions)
                }
              />
            </StyledHeaderBoxTableCell>
          </StyledHeaderTableCell>
          <StyledHeaderTableCell align="center">
            <StyledHeaderBoxTableCell>
              <Typography variant="body2" component="p" fontWeight={500}>
                Cliques
              </Typography>
              <SortIcon
                sortOrder={sortByOrderClicks}
                onClick={() =>
                  toggleSort(sortByOrderClicks, setSortByOrderClicks)
                }
              />
            </StyledHeaderBoxTableCell>
          </StyledHeaderTableCell>
          <StyledHeaderTableCell align="center">
            <StyledHeaderBoxTableCell>
              <Typography variant="body2" component="p" fontWeight={500}>
                CTR
              </Typography>
              <SortIcon
                sortOrder={sortByOrderCTR}
                onClick={() => toggleSort(sortByOrderCTR, setSortByOrderCTR)}
              />
            </StyledHeaderBoxTableCell>
          </StyledHeaderTableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {isLoading && (
          <TableRow>
            <TableCell component="th" scope="row" colSpan={7} align="center">
              <CircularProgress size={15} /> Carregando
            </TableCell>
          </TableRow>
        )}

        {!isLoading && !isError && data?.bestCampaigns?.length === 0 && (
          <TableRow>
            <TableCell colSpan={7} align="center">
              Sem resultados
            </TableCell>
          </TableRow>
        )}

        {isError && (
          <TableRow>
            <TableCell
              sx={{ color: theme => theme.palette.error.light }}
              colSpan={7}
              align="center"
            >
              <Typography>Ocorreu um erro: {error?.name}</Typography>
              <Typography>{error?.message}</Typography>
            </TableCell>
          </TableRow>
        )}

        {!isLoading &&
          !isError &&
          sortedCampaigns.map(campaign => {
            return <DashListRow key={campaign.id} campaign={campaign} />;
          })}
      </TableBody>
    </StyledTable>
  );
}
